import { AutoComplete, Button, Checkbox, Col, Form, Input, message, Modal, Popconfirm, Row, Select } from "antd";
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { standardQuestionAttestationCategories, standardQuestionIncomeCategories, standardQuestionInsuranceCategories, standardQuestionMedicalCategories, standardQuestionPatientCategories, standardQuestionProviderCategories, standardQuestionTabs } from '../../../src/Constants/DataModelToolConstants';
import { DataKey, SpecialCharacters, TypeList } from "../../Constants/DataModelToolConstants";
import { standardQuestionSliceDispatch } from "../../Constants/ReduxConstants";
import StandardQuestion from "./StandardQuestion";
import { UploadFile } from './StandardQuestionHelper';

const StandardQuestionModal = () => {
    const dispatch = useDispatch();
    const open = useSelector((state: any) => state.standardQuestionViewer.modalOpen);
    const isAdd = useSelector((state: any) => state.standardQuestionViewer.isAdd);
    const allQuestions = useSelector((state: any) => state.standardQuestionViewer.questions) ?? [];
    const question = useSelector((state: any) => state.standardQuestionViewer.selectedQuestion) || new StandardQuestion();
    const categories = useSelector((state: any) => state.standardQuestionViewer.categories);
    const [form] = Form.useForm();
    useEffect(() => {
        form.validateFields(['dataField']);
    }, [form]);

    let questionLowerCaseDataKeys = allQuestions.map(q => {
        return q.dataField.toLowerCase();
    });

    const confirm = () => {
        message.success('Uploading...');
        onSubmit();
    };

    const cancel = () => {
        message.error('Canceling...');
    };

    const onCancel = () => {
        dispatch({ type: standardQuestionSliceDispatch.setSelectedQuestion, payload: {} });
        dispatch({ type: standardQuestionSliceDispatch.setModalOpen, payload: false });
    }

    const onSubmit = async () => {
        await form.validateFields().then(async () => {
            let updatedQuestions = allQuestions;

            if (!isAdd) {
                updatedQuestions = allQuestions.filter(q => q.dataField != question.dataField);
            }

            updatedQuestions.push(question);

            await UploadFile(updatedQuestions);
            dispatch({ type: standardQuestionSliceDispatch.setSelectedQuestion, payload: {} });
            dispatch({ type: standardQuestionSliceDispatch.setQuestions, payload: updatedQuestions });
            dispatch({ type: standardQuestionSliceDispatch.setModalOpen, payload: false });
        });
    }

    const onChange = (event) => {
        let valueToUse = event?.target?.value;
        // Checkboxes need to use event.target.checked as event.target.value is always ""
        if (event?.target?.type === "checkbox") {
            valueToUse = event.target.checked;
        }

        dispatch({ type: standardQuestionSliceDispatch.setSelectedQuestion, payload: { ...question, [event.target.id]: valueToUse } });
    }

    const tabChanged = (event) => {
        form.setFieldsValue({ category: undefined })
        question.tab = event;
        dispatch({ type: standardQuestionSliceDispatch.setSelectedQuestion, payload: { ...question } });

        switch (question.tab) {
            case 'patient':
                dispatch({ type: standardQuestionSliceDispatch.setCategories, payload: standardQuestionPatientCategories });
                break;
            case 'insurance':
                dispatch({ type: standardQuestionSliceDispatch.setCategories, payload: standardQuestionInsuranceCategories });
                break;
            case 'income & eligibility':
                dispatch({ type: standardQuestionSliceDispatch.setCategories, payload: standardQuestionIncomeCategories });
                break;
            case 'medical':
                dispatch({ type: standardQuestionSliceDispatch.setCategories, payload: standardQuestionMedicalCategories });
                break;
            case 'provider':
                dispatch({ type: standardQuestionSliceDispatch.setCategories, payload: standardQuestionProviderCategories });
                break;
            case 'attestation & signature':
                dispatch({ type: standardQuestionSliceDispatch.setCategories, payload: standardQuestionAttestationCategories });
                break;
            default:
                dispatch({ type: standardQuestionSliceDispatch.setCategories, payload: [] });
                break;
        }
    }

    const categoryChanged = (event) => {
        question.category = event;
        dispatch({ type: standardQuestionSliceDispatch.setSelectedQuestion, payload: { ...question } });
    }

    const dataFieldChanged = (event) => {
        question.dataField = event;
        dispatch({ type: standardQuestionSliceDispatch.setSelectedQuestion, payload: { ...question } });
    }

    const typeChanged = (event) => {
        question.type = event;
        dispatch({ type: standardQuestionSliceDispatch.setSelectedQuestion, payload: { ...question } });
    }

    return (
        <Modal
            title="Question Details"
            footer={null}
            closable={true}
            open={open}
            destroyOnHidden={true}
            onCancel={onCancel}>
            <Form
                form={form}
                preserve={false}
                initialValues={{
                    ["category"]: question.category != null
                        ? question.category.split(" ").map((word) => {
                            return word[0]?.toUpperCase() + word?.substring(1);
                        }).join(" ")
                        : []
                }}
            >
                <Row>
                    <Col span={24}>
                        <Form.Item rules={[{ required: true }, {
                            validator(rule, value) {
                                return new Promise((resolve, reject) => {
                                    if (SpecialCharacters.some(x => value.includes(x))) {
                                        reject('Please exclude any special characters outside of "_" from the data key.');
                                    }
                                    else {
                                        if (isAdd) {
                                            if (questionLowerCaseDataKeys?.filter(x => x == value.toLowerCase()).length == 1) {
                                                reject('There is already a question with the data key ' + value + '.');
                                            }
                                            else {
                                                resolve(undefined);
                                            }
                                        }
                                        else {
                                            if (questionLowerCaseDataKeys?.filter(x => x == value).length == 1 && question.dataField != value) {
                                                reject('There is already a question with the data key ' + value + '.');
                                            }
                                            else {
                                                resolve(undefined);
                                            }
                                        }
                                    }
                                })
                            }
                        }]} name="Data Field" label="Data Field" initialValue={question.dataField}>
                            <AutoComplete
                                id="dataField"
                                disabled={!isAdd}
                                placeholder='Data Field'
                                dropdownMatchSelectWidth={true}
                                options={DataKey.map(k => ({ label: k, value: k }))}
                                onChange={dataFieldChanged}
                            />
                        </Form.Item>
                        <Form.Item name="Display Value" label="Display Value" initialValue={question.label}>
                            <Input id="label" placeholder='Label' onChange={onChange} />
                        </Form.Item>
                        <Form.Item rules={[{ required: true }]} initialValue={question.tab} name="Tab" label="Tab Section">
                            <Select
                                id="tab"
                                placeholder='Tab'
                                showSearch
                                options={standardQuestionTabs}
                                onChange={tabChanged}
                            />
                        </Form.Item>
                        {categories.length != 0
                            && (<Form.Item rules={[{ required: true }]} name="category" key="category" label="Category Section">
                                <Select
                                    id="category"
                                    placeholder='Category'
                                    showSearch
                                    options={categories}
                                    onChange={categoryChanged}
                                />
                            </Form.Item>)}
                        <Form.Item name="Min Length" label="Min Length" initialValue={question.minLength}>
                            <Input id="minLength" type="number" placeholder='Min Length' onChange={onChange} />
                        </Form.Item>
                        <Form.Item rules={[{ required: true }]} name="Display Type" label="Display Type" initialValue={question.type}>
                            <Select
                                showSearch
                                placeholder='Select Field Type'
                                options={TypeList.map(k => ({ label: k.text, value: k.text }))}
                                onChange={typeChanged}
                                id="type"
                            />
                        </Form.Item>
                        <Form.Item>
                            <Checkbox id="required" defaultChecked={Boolean(question.required)} onChange={onChange}>Required</Checkbox>
                        </Form.Item>
                        <Form.Item>
                            <Checkbox id="hidden" defaultChecked={Boolean(question.hidden)} onChange={onChange}>Hidden</Checkbox>
                        </Form.Item>
                        <Form.Item>
                            <Checkbox id="sensitive" defaultChecked={Boolean(question.sensitive)} onChange={onChange}>Sensitive</Checkbox>
                        </Form.Item>
                        <Form.Item>
                            <Checkbox id="readonly" defaultChecked={Boolean(question.readonly)} onChange={onChange}>Read Only</Checkbox>
                        </Form.Item>
                    </Col>
                </Row>
                <div style={{ display: 'flex', justifyContent: 'end', paddingTop: '1em' }}>
                    <Form.Item>
                        <Button onClick={onCancel}>
                            Cancel
                        </Button>
                    </Form.Item>
                    <Form.Item>
                        <Popconfirm
                            title="Are you sure you wish to upload this standard question?"
                            onConfirm={confirm}
                            onCancel={cancel}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button htmlType="submit">
                                Submit
                            </Button>
                        </Popconfirm>
                    </Form.Item>
                </div>
            </Form>
        </Modal >
    )
}

export default StandardQuestionModal;